<template>
	<RangeNotchedSlider
		:model-value="preferredModelValue"
		:min="preferredMin"
		:max="preferredMax"
		:thresholds="preferredThresholds"
		:open-ended-anchor="preferredOpenEndedAnchor"
		:unit-suffix="` ${areaUnitSymbol}`"
		:label="preferredLabel"
		:disabled="disabled"
		@update:model-value="updateModelValue"
		@change="change => emit('change', change)"
	/>
</template>

<script setup lang="ts">
import { computed } from "vue"
import RangeNotchedSlider from "@/components/ui/RangeNotchedSlider.vue"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { AreaUnit, convertArea, convertAreaRange } from "@/utils/unitSystem"
import { roundToCleanNumber } from "@/utils/format"

const OPEN_ENDED_ANCHOR_FACTOR = 4

const props = withDefaults(
	defineProps<{
		modelValue: [number, number]
		min?: number
		max: number
		thresholds: [number, number, number]
		label?: string
		disabled?: boolean
	}>(),
	{
		min: 0,
		label: "",
		disabled: false,
	}
)

const emit = defineEmits<{
	(e: "update:modelValue", value: [number, number]): void
	(e: "change", value: { from: [number, number]; to: [number, number] }): void
}>()

const sourceUnit = AreaUnit.SquareFeet
const { areaUnit, areaUnitSymbol } = useUnitSystem()
const isMetric = computed(() => areaUnit.value === AreaUnit.SquareMeters)
const preferredModelValue = computed(() => convertAreaRange(props.modelValue, sourceUnit, areaUnit.value))
const preferredMin = computed(() => convertAreaRange([props.min, props.min], sourceUnit, areaUnit.value)[0])
const preferredMax = computed(() => convertAreaRange([props.max, props.max], sourceUnit, areaUnit.value)[0])
const preferredThresholds = computed<[number, number, number]>(() => {
	const converted = convertAreaRange(props.thresholds, sourceUnit, areaUnit.value)
	return isMetric.value ? (converted.map(roundToCleanNumber) as [number, number, number]) : converted
})
const preferredOpenEndedAnchor = computed(() => {
	const anchor = props.thresholds[props.thresholds.length - 1] * OPEN_ENDED_ANCHOR_FACTOR
	const converted = convertArea(anchor, sourceUnit, areaUnit.value)
	return isMetric.value ? roundToCleanNumber(converted) : converted
})
const preferredLabel = computed(() => (props.label ? `${props.label} (${areaUnitSymbol.value})` : ""))

function updateModelValue(value: [number, number]): void {
	emit("update:modelValue", convertAreaRange(value, areaUnit.value, sourceUnit))
}
</script>
